import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataViewProperty,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
    };
}
const mapStateToProps = (state: ApplicationState) => ({
    hidePagination: isLoadMore(state),
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
    container?: InjectableCss;
}

export const paginationStyles: ProductListPaginationStyles = {
    pagination: {
        cssOverrides: {
            perPageSelect: css`
                padding: 4px 16px;
                > div[class^="FormFieldStyle"] {
                    border: 2px solid ${COLORS.background.grayShade};
                    border-radius: 40px;
                    background: ${getColor("primary.contrast")} !important;
                    width: 220px !important;
                    padding: 0;
                    label {
                        font-size: ${TYPOGRAPHY.size.smaller} !important;
                        font-weight: ${TYPOGRAPHY.weight.base} !important;
                        line-height: ${TYPOGRAPHY.lineHeight.base} !important;
                        color: ${getProp("theme.typography.p.color")} !important;
                        padding-top: 7px !important;
                    }
                    > div[class^="FormInputWrapper"] {
                        width: 100% !important;
                        select {
                            border: none !important;
                            border-radius: 40px !important;
                            font-size: ${TYPOGRAPHY.size.smaller} !important;
                            font-weight: ${TYPOGRAPHY.weight.bold} !important;
                            line-height: ${TYPOGRAPHY.lineHeight.base} !important;
                            color: ${getColor("background.contrast")} !important;
                            padding: 0px 7px;
                        }
                        span {
                            color: ${getProp("theme.typography.p.color")} !important;
                        }
                    }
                }
            `,
            pagination: css`
                border: 4px solid ${COLORS.background.grayShade};
                nav {
                    flex-basis: auto;
                }
                @media (min-width: 992px) and (max-width: 1200px) {
                    justify-content: flex-start;
                    > nav {
                        padding-right: 19px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            > span {
                                justify-content: center;
                                flex-basis: auto;
                            }
                            > nav {
                                justify-content: center;
                                order: 1;
                            }
                        `,
                        css`
                            justify-content: center;
                            > span {
                                justify-content: center;
                                flex-basis: auto;
                            }
                            > nav {
                                justify-content: center;
                                order: 1;
                            }
                        `,
                        null,
                        null,
                        null,
                    ])}
            `,
            currentButton: css`
                background: ${COLORS.primary.base};
                padding: 4px !important;
                margin: 4px !important;
                border-radius: 80px !important;
                min-height: 0;
                height: 32px;
                width: 32px;
                span {
                    font-size: ${TYPOGRAPHY.size.large};
                    line-height: ${TYPOGRAPHY.lineHeight.base};
                }
            `,
        },
    },
};

const styles = paginationStyles;

const ProductListPagination = ({ addProductFilters, hidePagination, pagination, fields }: Props) => {
    const container = {
        css: css`
            > div:first-child {
                background: ${fields.backgroundColor} !important;
            }
        `,
    };
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const changePage = (newPageIndex: number) => {
        addProductFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
        window.scrollTo(0, 0);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
    };

    if (hidePagination || !pagination || !pagination.totalItemCount) {
        return null;
    }

    return (
        <StyledWrapper {...container}>
            <Pagination
                {...styles.pagination}
                resultsCount={pagination.totalItemCount}
                currentPage={pagination.page}
                resultsPerPage={pagination.pageSize}
                resultsPerPageOptions={pagination.pageSizeOptions}
                onChangePage={changePage}
                onChangeResultsPerPage={changeResultsPerPage}
                pageSizeCookie="ProductList-PageSize"
                data-test-selector="productList_pagination"
                prevPage={pagination.prevPageUri}
                nextPage={pagination.nextPageUri}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                editorTemplate: "TextField",
                defaultValue: "#linear-gradient(90deg,#266b40 27.08%,#2d804c 71.87%,#43687a 100%)",
                fieldType: "General",
                displayName: "Background Color",
            },
        ],
    },
};

export default widgetModule;
